import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import { APP } from '../config/app';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('validating'); // validating | ready | submitting | done | error
  const [tokenUser, setTokenUser] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setTokenError('No reset token found. Please request a new reset link.');
      return;
    }

    authAPI.validateResetToken(token)
      .then(data => {
        if (data.valid) {
          setTokenUser(data.user);
          setStatus('ready');
        } else {
          setStatus('error');
          setTokenError(data.error || 'This reset link is invalid or has expired.');
        }
      })
      .catch(() => {
        setStatus('error');
        setTokenError('Could not validate reset link. Please try again.');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setSubmitError('Passwords do not match.');
      return;
    }

    setStatus('submitting');
    try {
      const data = await authAPI.resetPassword(token, password);
      if (data.success) {
        setStatus('done');
      } else {
        setSubmitError(data.error || 'Failed to reset password.');
        setStatus('ready');
      }
    } catch (err) {
      setSubmitError(err.message || 'Your password could not be reset. The link may have expired, so request a new one.');
      setStatus('ready');
    }
  };

  return (
    <div className="setup-password-page">
      <div className="setup-password-card">
        <img
          src={`${APP.basePath}/ncadLogo.gif`}
          alt="NCAD"
          className="setup-password-logo"
        />
        <h1 className="setup-password-title">Set New Password</h1>

        {status === 'validating' && (
          <p className="setup-password-message">Verifying your reset link...</p>
        )}

        {status === 'error' && (
          <div className="setup-password-error-block">
            <p>{tokenError}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/forgot-password')}
              style={{ marginTop: '1rem' }}
            >
              Request New Reset Link
            </button>
          </div>
        )}

        {(status === 'ready' || status === 'submitting') && tokenUser && (
          <>
            <p className="setup-password-welcome">
              Resetting password for <strong>{tokenUser.email}</strong>.
            </p>
            <form className="setup-password-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="rp-password">New Password</label>
                <input
                  id="rp-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="At least 8 characters"
                  autoFocus
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="rp-confirm">Confirm Password</label>
                <input
                  id="rp-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="form-input"
                  placeholder="Re-enter your password"
                  required
                />
              </div>
              {submitError && <p className="setup-password-field-error">{submitError}</p>}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={status === 'submitting'}
                style={{ width: '100%', marginTop: '0.5rem' }}
              >
                {status === 'submitting' ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        {status === 'done' && (
          <div className="setup-password-done">
            <p style={{ marginBottom: '1rem' }}>Your password has been reset successfully.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/')}
            >
              Go to Sign In
            </button>
          </div>
        )}

        {status !== 'done' && status !== 'error' && (
          <button
            type="button"
            className="btn unstyled"
            onClick={() => navigate('/')}
            style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--text-tertiary)', cursor: 'pointer', background: 'none', border: 'none' }}
          >
            ← Back to Sign In
          </button>
        )}
      </div>

      <style>{`
        .setup-password-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--surface-page);
          padding: 2rem;
        }
        .setup-password-card {
          background: var(--surface-base);
          border-radius: 12px;
          box-shadow: var(--shadow-md);
          padding: 2.5rem 2rem;
          width: 100%;
          max-width: 420px;
          text-align: center;
        }
        .setup-password-logo {
          height: 56px;
          margin-bottom: 1.5rem;
        }
        .setup-password-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 1rem;
        }
        .setup-password-welcome {
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
          line-height: 1.5;
        }
        .setup-password-message {
          color: var(--text-tertiary);
        }
        .setup-password-form {
          text-align: left;
        }
        .setup-password-form .form-group {
          margin-bottom: 1rem;
        }
        .setup-password-form label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.35rem;
          font-size: 0.875rem;
        }
        .setup-password-field-error {
          color: var(--color-error);
          font-size: 0.875rem;
          margin: 0.25rem 0 0.5rem;
        }
        .setup-password-error-block {
          color: var(--text-primary);
        }
        .setup-password-done {
          color: var(--color-success);
        }
      `}</style>
    </div>
  );
}
